import React, { useEffect, useState } from "react";
import { Table, Card, Button, Tag, Empty, Spin, Popconfirm, Input, Space } from "antd";
import { FilterDropdownProps, FilterValue, SorterResult } from "antd/es/table/interface";
import { SearchOutlined } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePen, faTrash, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
// REDUX
import {
  useLazyGetOrganizationsQuery,
  useDeleteOrganizationMutation,
} from "../../../redux/organizations/organizationAPI";
// AUTH
import GETJwtToken from "../../../redux/authentication/authentication";
// INTERFACES
import { OrganizationInterface } from "../../../interfaces/Organizations.interface";
// FLATBUFFER DISPLAY
import { formatUuidT, orgTypeLabel } from "../../../flatbuffers/display";
import { OrgType } from "../../../flatbuffers/generated/archive/org-type";
// STORE
import { RootState } from "../../../app/store";
// UTILS
import { getTableRowClass } from "../../../utils/utils";
// AUTHORIZATION
import { useScreenPermission } from "../../../customHooks/useScopedPermission";
import UnauthorizedPage from "../../unauthorized/UnauthorizedPage";
// COMPONENTS
import OrganizationDraftsTableActions from "./OrganizationDraftsTableActions";

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];

type SortDirection = "asc" | "desc";

interface DraftFilters {
  name: string;
  type: string;
  country: string;
  state: string;
}

interface DraftSort {
  field: string;
  direction: SortDirection;
}

const NO_FILTERS: DraftFilters = { name: "", type: "", country: "", state: "" };
const NO_SORT: DraftSort = { field: "", direction: "asc" };

// A draft can only be an ORG or a SUBORG; MASTER is the root and is never drafted. The filter
// value is the label because LiftCloud matches enum columns exactly, upper-cased.
const TYPE_FILTER_OPTIONS = [OrgType.ORG, OrgType.SUBORG].map((orgType) => ({
  text: orgTypeLabel(orgType),
  value: orgTypeLabel(orgType),
}));

const firstFilterValue = (value: FilterValue | null | undefined): string =>
  value && value.length > 0 ? String(value[0]).trim() : "";

/**
 * Column props for a free-text search dropdown. The search is applied on Search / Enter, not on
 * every keystroke, and `filteredValue` keeps the column header in step with the applied filter.
 */
const textSearchColumn = (label: string, applied: string) => ({
  filteredValue: applied ? [applied] : null,
  filterDropdown: ({
    selectedKeys,
    setSelectedKeys,
    confirm,
    clearFilters,
  }: FilterDropdownProps) => (
    <div style={{ padding: 8 }}>
      <Input
        placeholder={`Search ${label}`}
        value={String(selectedKeys[0] ?? "")}
        onChange={(event) => setSelectedKeys(event.target.value ? [event.target.value] : [])}
        onPressEnter={() => confirm()}
        style={{ marginBottom: 8, display: "block", width: 200 }}
      />
      <Space>
        <Button
          type="primary"
          size="small"
          icon={<SearchOutlined />}
          onClick={() => confirm()}
          style={{ width: 90 }}
        >
          Search
        </Button>
        <Button
          size="small"
          onClick={() => {
            if (clearFilters) clearFilters();
            confirm();
          }}
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </Space>
    </div>
  ),
  filterIcon: (filtered: boolean) => (
    <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
  ),
});

export default function OrganizationDraftsPage() {
  const { theme, globalOrganizationId } = useSelector((state: RootState) => state.home);
  const navigate = useNavigate();
  // Reads SCREEN_PERMISSION["drafts"] -- the same declaration Layout.tsx's
  // sidebar link reads. Previously this page had no self-check of its own
  // (see the "drafts" comment in screenPermissions.ts); the sidebar hiding
  // the link was the only enforcement, so a direct URL visit bypassed it
  // for any authenticated user regardless of permission.
  const allowed = useScreenPermission("drafts");

  const [drafts, setDrafts] = useState<OrganizationInterface[]>([]);
  // The server pages the list (`page`/`limit`, with the full count in `total`), so the table
  // asks for one page at a time instead of rendering the first N and dropping the rest.
  const [pagination, setPagination] = useState({ current: 1, pageSize: DEFAULT_PAGE_SIZE });
  // Search and sort run on the server too: filtering or sorting only the rows of the current page
  // would hide matches on every other page.
  const [filters, setFilters] = useState<DraftFilters>(NO_FILTERS);
  const [sort, setSort] = useState<DraftSort>(NO_SORT);
  const hasFilters = Object.values(filters).some((value) => value !== "");

  const [triggerGetOrgs, { data, isLoading, isFetching }] = useLazyGetOrganizationsQuery();

  const [triggerDeleteOrg, { isLoading: isDeleting, isSuccess: isDeleteSuccess }] =
    useDeleteOrganizationMutation();

  const fetchDrafts = async (page = pagination.current, limit = pagination.pageSize) => {
    const token = await GETJwtToken();
    triggerGetOrgs({
      page,
      limit,
      token,
      parentId: globalOrganizationId,
      searchAndFilterString: "",
      statusFilter: "DRAFT",
      filterName: filters.name,
      filterOrgType: filters.type,
      filterCountry: filters.country,
      filterState: filters.state,
      sortField: sort.field,
      sortDirection: sort.direction,
    });
  };

  useEffect(() => {
    if (globalOrganizationId) {
      fetchDrafts();
    }
  }, [globalOrganizationId, pagination.current, pagination.pageSize, filters, sort]);

  useEffect(() => {
    // A search with no matches comes back with no rows: show that, not the previous results.
    if (data) {
      setDrafts(data.data ?? []);
    }
  }, [data]);

  const handleTableChange = (
    next: { current?: number; pageSize?: number },
    tableFilters: Record<string, FilterValue | null>,
    sorter: SorterResult<OrganizationInterface> | SorterResult<OrganizationInterface>[],
  ) => {
    const nextFilters: DraftFilters = {
      name: firstFilterValue(tableFilters.name),
      type: firstFilterValue(tableFilters.type),
      country: firstFilterValue(tableFilters.country),
      state: firstFilterValue(tableFilters.state),
    };
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextSort: DraftSort = activeSorter?.order
      ? {
          field: String(activeSorter.columnKey ?? ""),
          direction: activeSorter.order === "descend" ? "desc" : "asc",
        }
      : NO_SORT;
    const queryChanged =
      JSON.stringify(nextFilters) !== JSON.stringify(filters) ||
      nextSort.field !== sort.field ||
      nextSort.direction !== sort.direction;

    setFilters(nextFilters);
    setSort(nextSort);
    // A new search or sort starts again from the first page of its results.
    setPagination({
      current: queryChanged ? 1 : (next.current ?? 1),
      pageSize: next.pageSize ?? DEFAULT_PAGE_SIZE,
    });
  };

  useEffect(() => {
    if (isDeleteSuccess) {
      // Deleting the last row of a page leaves that page empty: step back one.
      if (drafts.length === 1 && pagination.current > 1) {
        setPagination((p) => ({ ...p, current: p.current - 1 }));
      } else {
        fetchDrafts();
      }
    }
  }, [isDeleteSuccess]);

  const handleClearSearch = () => {
    setFilters(NO_FILTERS);
    setPagination((p) => ({ ...p, current: 1 }));
  };

  const handleDelete = async (id: string) => {
    const token = await GETJwtToken();
    triggerDeleteOrg({ id, token });
  };

  const handleResume = (orgId: string) => {
    navigate(`/organization-management/setup-wizard?orgId=${orgId}`);
  };

  const handleDeleteRecord = (record: OrganizationInterface) => {
    handleDelete(formatUuidT(record.id));
  };

  const handleResumeRecord = (record: OrganizationInterface) => {
    handleResume(formatUuidT(record.id));
  };

  // With no search applied an empty list means there are no drafts at all, so there is nothing to
  // search or sort and the table gives way to a plain message.
  const showBareEmptyState = drafts.length === 0 && !hasFilters && !isLoading && !isFetching;

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      ...textSearchColumn("Name", filters.name),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      sorter: true,
      filters: TYPE_FILTER_OPTIONS,
      filterMultiple: false,
      filteredValue: filters.type ? [filters.type] : null,
      render: (text: OrgType) => (
        <Tag color={text === OrgType.ORG ? "blue" : "orange"}>{orgTypeLabel(text)}</Tag>
      ),
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      sorter: true,
      ...textSearchColumn("Country", filters.country),
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      sorter: true,
      ...textSearchColumn("State", filters.state),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      // The key is the sort target sent to LiftCloud, which orders by the database column.
      key: "created_at",
      sorter: true,
      render: (text: string) => (text ? new Date(text).toLocaleDateString() : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: OrganizationInterface) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            type="primary"
            size="small"
            icon={<FontAwesomeIcon icon={faArrowRight} />}
            onClick={() => handleResumeRecord(record)}
          >
            Resume
          </Button>
          <Popconfirm
            title="Delete this draft?"
            onConfirm={() => handleDeleteRecord(record)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button danger size="small" icon={<FontAwesomeIcon icon={faTrash} />}>
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
    {
      // The row's ⋯ menu: Export downloads the whole draft as one binary export.
      title: "",
      key: "more",
      align: "right" as const,
      width: 48,
      render: (_: unknown, record: OrganizationInterface) => (
        <OrganizationDraftsTableActions selectedItem={record} />
      ),
    },
  ];

  return (
    <div className={`layout__contentPage__${theme}`}>
      {allowed ? (
        <Card style={{ margin: "16px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <h2 style={{ margin: 0 }}>
              <FontAwesomeIcon icon={faFilePen} style={{ marginRight: 8 }} />
              Organization Drafts
            </h2>
            <Space>
              {hasFilters && <Button onClick={handleClearSearch}>Clear search</Button>}
              <Button
                type="primary"
                onClick={() => navigate("/organization-management/setup-wizard")}
              >
                + New Organization
              </Button>
            </Space>
          </div>
          <Spin spinning={isLoading || isFetching || isDeleting}>
            {showBareEmptyState ? (
              <Empty description="No draft organizations" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <Table
                dataSource={drafts}
                className="my-custom-table"
                columns={columns}
                rowKey={(record) => formatUuidT(record.id)}
                size="small"
                // The empty state lives inside the table so a search with no matches keeps its
                // column headers, and with them the way to clear the search.
                locale={{
                  emptyText: (
                    <Empty
                      description="No drafts match the current search"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  ),
                }}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: data?.total ?? drafts.length,
                  showSizeChanger: true,
                  pageSizeOptions: PAGE_SIZE_OPTIONS,
                  showTotal: (total: number) => `${total} drafts`,
                }}
                onChange={handleTableChange}
                rowClassName={(_, index) => getTableRowClass(index, theme)}
              />
            )}
          </Spin>
        </Card>
      ) : (
        <UnauthorizedPage />
      )}
    </div>
  );
}
